/*
  Community dialog — lets a full section render inside a <dialog> instead of in
  the page flow, opened by any element matching a merchant-supplied CSS selector.

  Markup contract (rendered by the section):
    <dialog class="cmy-dialog"
            data-cmy-dialog
            data-cmy-dialog-trigger="[data-open-giveaway], a[href$='#giveaway']"
            data-cmy-dialog-hash="giveaway,community-giveaway-1234">
      <div class="cmy-dialog__panel">
        <button data-cmy-dialog-close>…</button>
        <div class="cmy-dialog__body"> …section… </div>
      </div>
    </dialog>

  Triggers are matched through one delegated document listener, so buttons that
  are added later (other sections, apps, the header) still work. The hash list
  opens the dialog on load / hashchange — that covers deep links and Shopify's
  post-submit redirect to #<form id>, which is what reveals the success panel.

  The same key list also answers a `?dialog=` query parameter, so another page
  can deep-link straight into a popup:

    /pages/our-community?dialog=giveaway   → opens the giveaway dialog
    /pages/our-community?dialog=partner    → opens the partner dialog

  The key is the section's Anchor ID. The parameter is stripped from the URL
  once it has opened a dialog, so a refresh does not force it back open.
*/
(function () {
  'use strict';

  if (window.CommunityDialog) return;

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]),' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var registry = [];
  var openCount = 0;
  var prevOverflow = '';
  var prevPadding = '';

  /* A modal <dialog> does not stop the page behind it from scrolling. */
  function lockScroll() {
    if (openCount++ > 0) return;
    var html = document.documentElement;
    var bar = window.innerWidth - html.clientWidth;
    prevOverflow = html.style.overflow;
    prevPadding = html.style.paddingRight;
    html.style.overflow = 'hidden';
    if (bar > 0) html.style.paddingRight = bar + 'px';
  }

  function unlockScroll() {
    if (openCount === 0 || --openCount > 0) return;
    var html = document.documentElement;
    html.style.overflow = prevOverflow;
    html.style.paddingRight = prevPadding;
  }

  function splitList(value) {
    return (value || '')
      .split(',')
      .map(function (part) {
        return part.trim();
      })
      .filter(Boolean);
  }

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function Dialog(el) {
    this.el = el;
    this.native = typeof el.showModal === 'function';
    this.triggerSelector = (el.getAttribute('data-cmy-dialog-trigger') || '').trim();
    this.hashes = splitList(el.getAttribute('data-cmy-dialog-hash'));
    this.duration = parseInt(el.getAttribute('data-cmy-dialog-duration'), 10) || 0;
    this.opener = null;
    this.closing = false;
    this.closeTimer = null;
    this.onKeydown = this.handleKeydown.bind(this);

    if (!this.native) el.classList.add('cmy-dialog--fallback');

    el.addEventListener('click', this.handleClick.bind(this));
    el.addEventListener('close', this.afterClose.bind(this));
    /* Escape closes a modal <dialog> instantly — take it over so the exit plays. */
    el.addEventListener(
      'cancel',
      function (event) {
        event.preventDefault();
        this.close();
      }.bind(this)
    );
  }

  Dialog.prototype.isOpen = function () {
    return this.el.hasAttribute('open');
  };

  Dialog.prototype.matchesTrigger = function (target) {
    if (!this.triggerSelector || !target || !target.closest) return null;
    try {
      return target.closest(this.triggerSelector);
    } catch (error) {
      /* A merchant can type an invalid selector — ignore it rather than throw. */
      return null;
    }
  };

  /* One key list serves both the hash and the ?dialog= parameter. */
  Dialog.prototype.matchesKey = function (key) {
    return this.hashes.indexOf(key) !== -1;
  };

  Dialog.prototype.open = function (opener) {
    /* Re-opening mid-exit just cancels the exit; the entry has already finished. */
    if (this.closing) {
      this.cancelClosing();
      return;
    }
    if (this.isOpen()) return;
    this.opener = opener || document.activeElement;

    if (this.native) {
      this.el.showModal();
    } else {
      this.el.setAttribute('open', '');
      document.addEventListener('keydown', this.onKeydown);
    }

    lockScroll();

    /* Close button first: focusing the first input would open the keyboard on mobile. */
    var body = this.el.querySelector('.cmy-dialog__body');
    var target =
      this.el.querySelector('[data-cmy-dialog-close]') ||
      (body && body.querySelector(FOCUSABLE)) ||
      this.el;
    target.focus({ preventScroll: true });
  };

  /*
    Closing is deferred so the exit animation can run while the dialog is still
    open — i.e. still in the top layer. animationend drives it; the timer is only
    a safety net for when no animation actually runs.
  */
  Dialog.prototype.close = function () {
    if (!this.isOpen() || this.closing) return;

    var ms = reducedMotion() ? 0 : this.duration;
    if (!ms) {
      this.commitClose();
      return;
    }

    var self = this;
    this.closing = true;
    this.el.setAttribute('data-cmy-dialog-closing', '');
    this.onAnimationEnd = function (event) {
      if (event.target !== self.el) return;
      self.commitClose();
    };
    this.el.addEventListener('animationend', this.onAnimationEnd);
    this.closeTimer = setTimeout(function () {
      self.commitClose();
    }, ms + 80);
  };

  Dialog.prototype.commitClose = function () {
    if (!this.isOpen()) return;
    this.cancelClosing();
    if (this.native) {
      this.el.close();
      return;
    }
    this.el.removeAttribute('open');
    this.afterClose();
  };

  Dialog.prototype.cancelClosing = function () {
    if (this.closeTimer) clearTimeout(this.closeTimer);
    if (this.onAnimationEnd) this.el.removeEventListener('animationend', this.onAnimationEnd);
    this.closeTimer = null;
    this.onAnimationEnd = null;
    this.closing = false;
    this.el.removeAttribute('data-cmy-dialog-closing');
  };

  /* Runs for both paths: native `close` event, and manually after the fallback. */
  Dialog.prototype.afterClose = function () {
    /* Defensive: a browser that ignores the cancelled `cancel` closes straight through. */
    if (this.closing) this.cancelClosing();
    document.removeEventListener('keydown', this.onKeydown);
    unlockScroll();
    if (this.opener && document.contains(this.opener)) {
      this.opener.focus({ preventScroll: true });
    }
    this.opener = null;
  };

  Dialog.prototype.handleClick = function (event) {
    if (event.target.closest && event.target.closest('[data-cmy-dialog-close]')) {
      event.preventDefault();
      this.close();
      return;
    }
    /* A click on the backdrop is reported against the dialog element itself. */
    if (event.target === this.el) this.close();
  };

  /* Escape + focus trap for the fallback only; a modal <dialog> does both natively. */
  Dialog.prototype.handleKeydown = function (event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;

    var items = Array.prototype.filter.call(this.el.querySelectorAll(FOCUSABLE), function (node) {
      return node.offsetWidth || node.offsetHeight || node.getClientRects().length;
    });
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  function register(el) {
    if (el.dataset.cmyDialogReady === 'true') return;
    el.dataset.cmyDialogReady = 'true';
    registry.push(new Dialog(el));
  }

  function init(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(root.querySelectorAll('[data-cmy-dialog]'), register);
    openFromLocation();
  }

  var QUERY_KEY = 'dialog';

  function queryValue() {
    var pairs = window.location.search.replace(/^\?/, '').split('&');
    for (var i = 0; i < pairs.length; i++) {
      if (!pairs[i]) continue;
      var pair = pairs[i].split('=');
      if (decode(pair[0]) !== QUERY_KEY) continue;
      return decode(pair[1]).trim();
    }
    return '';
  }

  /* A hand-typed URL can carry a malformed escape, which throws in decodeURIComponent. */
  function decode(value) {
    try {
      return decodeURIComponent((value || '').replace(/\+/g, ' '));
    } catch (error) {
      return '';
    }
  }

  /*
    Drop the parameter once it has opened something, so a refresh — or the back
    button after the visitor closed the dialog — does not force it open again.
  */
  function clearQuery() {
    if (!window.history || !window.history.replaceState) return;
    var kept = window.location.search
      .replace(/^\?/, '')
      .split('&')
      .filter(function (pair) {
        return pair && decode(pair.split('=')[0]) !== QUERY_KEY;
      });
    var search = kept.length ? '?' + kept.join('&') : '';
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + search + window.location.hash
    );
  }

  function openByKey(key) {
    if (!key) return false;
    for (var i = 0; i < registry.length; i++) {
      if (registry[i].matchesKey(key)) {
        registry[i].open(null);
        return true;
      }
    }
    return false;
  }

  /* Hash wins: it is what Shopify's post-submit redirect uses. */
  function openFromLocation() {
    if (openByKey(window.location.hash.replace('#', ''))) return;
    if (openByKey(queryValue())) clearQuery();
  }

  document.addEventListener('click', function (event) {
    if (!(event.target instanceof Element)) return;
    for (var i = 0; i < registry.length; i++) {
      var trigger = registry[i].matchesTrigger(event.target);
      if (!trigger) continue;
      event.preventDefault();
      registry[i].open(trigger);
      return;
    }
  });

  window.addEventListener('hashchange', openFromLocation);
  /* Back/forward can restore a ?dialog= URL without reloading the page. */
  window.addEventListener('popstate', openFromLocation);

  /* Theme editor: reveal the section when the merchant selects it. */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });
    document.addEventListener('shopify:section:select', function (event) {
      var el = event.target.querySelector('[data-cmy-dialog]');
      var entry = el && find(el);
      if (entry) entry.open(null);
    });
    document.addEventListener('shopify:section:deselect', function (event) {
      var el = event.target.querySelector('[data-cmy-dialog]');
      var entry = el && find(el);
      if (entry) entry.close();
    });
  }

  function find(el) {
    for (var i = 0; i < registry.length; i++) {
      if (registry[i].el === el) return registry[i];
    }
    return null;
  }

  window.CommunityDialog = { init: init, find: find };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }
})();
